if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      if (event?.detail?.currentElement !== undefined) {
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
        this.setActiveThumbnail(thumbnail);
      }
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();

      // This automatically scrolls to the first variant image on variant change
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });

      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }
    
    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product-modal-opener-image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  }
  customElements.define('media-gallery', MediaGallery)

  customElements.define('variant-media-gallery', class VariantMediaGallery extends MediaGallery {
    connectedCallback() {
      this.bindListeners()
      this.initStackedGallery()
    }

    bindListeners() {
      const variantRadios = document.getElementById(`VariantRadios-${this.dataset.sectionId}`)
      if(variantRadios) {
        variantRadios.addEventListener('variantChange', this.onVariantChange.bind(this))
      }
    }

    /**
     * when variant changes, iterate over slides
     * if the variantId data property contains the current variant ID, then show
     *
     * after, update media count for a11y
     */
    onVariantChange(e) {
      const variantId = e.detail.id;
      if (!variantId) return;

      // Update current variant tracking variable
      this.currentVariantId = String(variantId);

      const slides = Array.from(this.querySelectorAll('.product-media-item'));
      const heroImages = Array.from(document.querySelectorAll('.product-media-modal-content img'));

      slides.forEach(slide => slide.classList.remove('for-variant'));

      heroImages.forEach(image => {
        image.classList.remove('for-variant');
        if (image.dataset?.variant_id?.includes(variantId)) {
          image.classList.add('for-variant');
        }
      });

      let mediaCount = 0;
      const thumbs = Array.from(this.querySelectorAll('.thumbnail-list-item'));
      const shownImages = {};

      slides.forEach(slide => {
        if (
          typeof slide.dataset.variantIds !== 'undefined' &&
          slide.dataset.variantIds !== null &&
          slide.dataset.variantIds.split(',').map(id => id.trim()).includes(String(variantId))
        ) {
          const imgEl = slide.querySelector('img');
          const imageSrc = imgEl ? imgEl.getAttribute('src') : null;

          if (imageSrc && !shownImages[imageSrc]) {
            mediaCount++;
            slide.classList.add('for-variant');
            shownImages[imageSrc] = true;
          }
        }
      });

      thumbs.forEach(thumb => {
        thumb.classList.remove('for-variant');
        if (
          typeof thumb.dataset.variantIds !== 'undefined' &&
          thumb.dataset.variantIds !== null &&
          thumb.dataset.variantIds.split(',').map(id => id.trim()).includes(String(variantId))
        ) {
          const thumbImgEl = thumb.querySelector('img');
          const thumbImageSrc = thumbImgEl ? thumbImgEl.getAttribute('src') : null;

          if (thumbImageSrc && !shownImages[thumbImageSrc]) {
            thumb.classList.add('for-variant');
            shownImages[thumbImageSrc] = true;
          }
        }
      });

      setTimeout(() => {
        const thumbnailContainer = document.getElementById(`Slider-Thumbnails-${this.dataset.sectionId}`);

        if (thumbnailContainer) {
          if (thumbnailContainer.scrollWidth > thumbnailContainer.clientWidth) {
            thumbnailContainer.parentNode.classList.add("show-arrows");
            Array.from(thumbnailContainer.parentNode.querySelectorAll(".slider-button")).forEach((button) =>
              button.removeAttribute("disabled")
            );
          } else {
            thumbnailContainer.parentNode.classList.remove("show-arrows");
            Array.from(thumbnailContainer.parentNode.querySelectorAll(".slider-button")).forEach((button) =>
              button.setAttribute("disabled", "disabled")
            );
          }
          
          if (thumbnailContainer.parentNode.resetPages) {
            thumbnailContainer.parentNode.resetPages();
          }
          thumbnailContainer.scrollLeft = 0;
        }

        const totalCounter = this.querySelector('.slider-counter-total');
        if (totalCounter) {
          totalCounter.textContent = mediaCount;
        }

        /**
         * Re-initialize gallery view and prepend active image
         */
        this.initStackedGallery();
      });
    }

    /**
     * Initializes double column desktop gallery
     */
    initStackedGallery() {
      if (!this.checkGalleryStacked()) return;

      this.setVariables();
      this.hideItems(true);
      this.prependPrimaryImage();

      if (!this.loaded) {
        this.setupShowButton();
        this.setupHideButton();
      }

      this.loaded = true;
    }

    /**
     *  Prepend the primary image of the active variant on load/switch
     */
    prependPrimaryImage() {
      const activeItems = this.getGalleryItems();

      if (activeItems.length > 0) {
        const firstActiveMediaId = activeItems[0].getAttribute('data-media-id');

        if (firstActiveMediaId) {
          const firstActiveMedia = this.elements.viewer.querySelector(`[data-media-id="${firstActiveMediaId}"]`);

          if (firstActiveMedia && firstActiveMedia.parentElement) {
            /**
             *  Move the first matching image to the top of the gallery list in DOM
             */
            firstActiveMedia.parentElement.prepend(firstActiveMedia);
          }
        }
      }
    }

    /**
     * Setup variables
     * 
     * @param {boolean} isReload 
     */
    setVariables() {
      const sliderComponent = this.querySelector('.slider-component-mobile:nth-of-type(1)');
      const variantInput = document.querySelector('form[action*="/cart"] input[name="id"]');
      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get('variant');

      this.loadMoreButton = this.querySelector('.gallery-show-more');
      this.showLessButton = this.querySelector('.gallery-show-less');
      this.mediaList = sliderComponent.querySelector('.product-media-list');
      
      this.currentVariantId = urlVariantId || (variantInput ? variantInput.value : null);

      this.initialVisibleCount = 8;
      this.visibleItems = this.initialVisibleCount;
    }

    /**
     * Is gallery type stacked
     * 
     * @returns {boolean}
     */
    checkGalleryStacked() {
      return this.getAttribute('data-desktop-layout') === 'stacked'
    }

    /**
     * Get showing gallery items
     * 
     * @returns {Array}
     */
    getGalleryItems() {
      const allMediaItems = Array.from(this.mediaList.querySelectorAll('.product-media-item'));

      if (!this.currentVariantId) return allMediaItems;

      const matched = allMediaItems.filter(item => {
        const ids = item.getAttribute('data-variant-ids');

        if (!ids) return false;

        return ids.split(',').map(id => id.trim()).includes(this.currentVariantId);
      });

      /**
       * Fallback: If image has no variant assignment, or if no matches exist, show default set
       */
      return matched.length > 0 ? matched : allMediaItems;
    }

    /**
     * Get number of showing media items
     * 
     * @returns {Number}
     */
    checkProductCount() {
      return this.getGalleryItems().length;
    }

    /**
     * Add listener to show more button
     */
    setupShowButton() {
      this.loadMoreButton.addEventListener('click', () => {
        this.showMoreItems();
      });
    }

    /**
     * Add listener to show less button
     */
    setupHideButton() {
      this.showLessButton.addEventListener('click', () => {
        this.hideItems();
      });
    }

    /**
     * Hide Load More if everything is visible
     * 
     * Show Show Less once we're above the initial amount
     */
    showMoreItems() {
      const items = this.getGalleryItems();

      this.visibleItems = items.length;

      items.forEach((item) => {
        item.hidden = false;
        item.style.removeProperty('display');
        item.classList.add('stack-show');
      });

      this.loadMoreButton.hidden = true;
      this.showLessButton.hidden = items.length <= this.initialVisibleCount;
    }

    /**
     * Hides gallery items if the showing count is higher than initial show count
     * 
     * @param {boolean} initial 
     */
    hideItems(initial = false) {
      const activeVariantItems = this.getGalleryItems();
      const allMediaItems = Array.from(this.mediaList.querySelectorAll('.product-media-item'));

      this.visibleItems = this.initialVisibleCount;

      /**
       * 1. First hide ALL gallery items in the DOM
       */ 
      allMediaItems.forEach(item => {
          item.hidden = true;
          item.classList.remove('stack-show');
      });

      /**
       * 2. Only reveal items matching the active variant up to initial count
       */ 
      activeVariantItems.forEach((item, index) => {
        if (index < this.visibleItems) {
          item.hidden = false;
          item.style.removeProperty('display');
          item.classList.add('stack-show');
        }
      });

      /**
       * Update show/hide toggle buttons accordingly
       */ 
      if (this.loadMoreButton) {
        this.loadMoreButton.hidden = activeVariantItems.length <= this.initialVisibleCount;
      }

      if (this.showLessButton) {
        this.showLessButton.hidden = true;
      }
    }
  });
}
